import { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Active, closestCenter, DndContext, Over } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import { v4 as uuid } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  ConditionNumericOperator,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  FunctionConditionExpression,
  FunctionIfsRepeaterProps,
  FunctionIfsValue,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_BETWEEN_RHS,
  OPERATOR_WITH_INPUT_RHS,
  TableAdapter,
} from '@makyoapp/makyo-common'
import { Plus16, Reorder16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { getOperatorDefaultOption, validateRhsEndValues } from '@/shared/condition'

import { useSortableHandle } from '../../hooks/useSortableHandle'
import { ColumnOrCustomDd } from './ColumnOrCustomDd'
import { FunctionConditionOperatorSelect } from './FunctionConditionOperatorSelect'
import { FunctionConditionSelect } from './FunctionConditionSelect'

interface FunctionConditionItemProps {
  id?: string
  onChange: (nextValue: FunctionIfsValue) => void
  value: FunctionIfsValue
  colOptions: ColumnAdapter[]
  table: TableAdapter
  defaultOperator: 'AND' | 'OR' | 'XOR'
  rhsCustomFieldOnly?: boolean
  columnID?: string
  expression: FunctionConditionExpression
}

export function FunctionConditionItem({
  expression,
  id,
  onChange,
  value,
  colOptions,
  table,
  defaultOperator,
  rhsCustomFieldOnly,
  columnID,
}: FunctionConditionItemProps) {
  const { tables } = useDbClientContext()
  const { t } = useTranslation()

  const index = useMemo(() => {
    return value?.conditions.findIndex((p) => p.id === expression.id)
  }, [expression, value])

  const cond = value.conditions[index]

  const colObj = useMemo(() => {
    return colOptions.find((c) => c.id === cond.lhs)
  }, [colOptions, cond.lhs])

  const varColTypes = useMemo(() => {
    if (colObj) {
      if (
        colObj.type === ColumnTypes.Text ||
        [ConditionTextOperator.IsNotContainedIn, ConditionTextOperator.IsContainedIn].includes(
          cond.operator as ConditionTextOperator
        )
      ) {
        return [ColumnTypes.Text, ColumnTypes.Array]
      } else if ([ColumnTypes.Rating, ColumnTypes.Number].includes(colObj.type)) {
        return [ColumnTypes.Rating, ColumnTypes.Number]
      } else {
        return [colObj.type]
      }
    } else {
      return []
    }
  }, [colObj, cond])

  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id: cond.id ?? uuid(),
  })

  const isDateSingleOperator = useMemo(() => {
    return DATE_OPERATOR_WITH_RHS.includes(cond.operator)
  }, [cond.operator])

  const isDateRangeOperator = useMemo(() => {
    return DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)
  }, [cond.operator])

  const isDateOperator = isDateSingleOperator || isDateRangeOperator

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className='flex-c min-w-0'
    >
      <div className='flex-c p-2 border bdr rounded-md bg-zinc-50 dark:bg-slate-700 min-w-0 w-full'>
        <div className='text-m-sm self-start shrink-0 w-[24px] pt-2.5'>{index === 0 ? 'IF' : defaultOperator}</div>
        <div className='flex flex-col gap-2 min-w-0 w-full'>
          <div className='flex-c w-full'>
            <div className='w-full min-w-0'>
              <FunctionConditionSelect
                toggleClassName='dark:hover:bg-slate-800!'
                id={`${id}-lhs`}
                value={cond?.lhs}
                table={table}
                onChange={(lhs) => {
                  const nextCond = [...value.conditions]
                  const isSame = nextCond[index].lhs === lhs
                  const newColObj = colOptions.find((c) => c.id === lhs)
                  const currentColObj = colOptions.find((c) => c.id === cond.lhs)

                  const shouldReset = newColObj && currentColObj && newColObj.type !== currentColObj.type

                  const nextOp = shouldReset
                    ? getOperatorDefaultOption(newColObj, table, tables)
                    : nextCond[index].operator

                  let extra: Record<string, unknown> = {}

                  if (shouldReset || !isSame) {
                    extra = validateRhsEndValues(nextOp)
                  }

                  nextCond[index] = {
                    ...nextCond[index],
                    lhs: lhs.toString(),
                    operator: nextOp,
                    ...(shouldReset ? extra : {}),
                  }

                  onChange({
                    operator: defaultOperator,
                    conditions: nextCond,
                  })
                }}
                colOptions={colOptions}
                excludeColumns={columnID ? [columnID] : undefined}
                includeUnknown={true}
              />
            </div>
            <FunctionConditionOperatorSelect
              id={id ? `${id}-op` : undefined}
              selected={cond.operator}
              table={table}
              value={value.conditions[index]}
              colOptions={colOptions}
              onChange={(opt) => {
                const nextCond = [...value.conditions]
                const extra = validateRhsEndValues(opt)
                nextCond[index] = {
                  ...nextCond[index],
                  operator: opt,
                  ...extra,
                }
                onChange({
                  operator: defaultOperator,
                  conditions: nextCond,
                })
              }}
            />
          </div>
          {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
            <div className='uppercase text-m-ss'>
              <Trans>Lower Bound</Trans>
            </div>
          )}
          {(OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ||
            DATE_OPERATOR_WITH_RHS.includes(cond.operator) ||
            NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
            DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
            <ColumnOrCustomDd
              inputClassName='dark:hover:bg-slate-800!'
              inputFocusClassName='dark:bg-slate-800!'
              withCustomValue
              withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
              customTypeOnly={rhsCustomFieldOnly}
              id={`${id}-rhs`}
              table={table}
              value={
                cond.rhs || {
                  isCustom: true,
                  value: '',
                }
              }
              customValueType={
                isDateOperator ? 'date' : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ? 'number' : 'text'
              }
              // types={varColTypes}
              onChange={(v) => {
                const nextCond = [...value.conditions]
                nextCond[index] = {
                  ...nextCond[index],
                  rhs: v,
                }
                onChange({
                  operator: defaultOperator,
                  conditions: nextCond,
                })
              }}
              excludeColumns={columnID ? [columnID, cond.lhs] : [cond.lhs]}
            />
          )}
          {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
            <div className='uppercase text-m-ss'>
              <Trans>Upper Bound</Trans>
            </div>
          )}
          {(NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
            DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
            <ColumnOrCustomDd
              inputClassName='dark:hover:bg-slate-800!'
              inputFocusClassName='dark:bg-slate-800!'
              withCustomValue
              withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
              customTypeOnly={rhsCustomFieldOnly}
              id={`${id}-rhs-end`}
              table={table}
              value={
                cond.rhsEnd || {
                  isCustom: true,
                  value: '',
                }
              }
              customValueType={
                isDateOperator ? 'date' : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ? 'number' : 'text'
              }
              toggleClassName='dark:hover:bg-slate-800!'
              onChange={(v) => {
                const nextCond = [...value.conditions]
                nextCond[index] = {
                  ...nextCond[index],
                  rhsEnd: v,
                }
                onChange({
                  operator: defaultOperator,
                  conditions: nextCond,
                })
              }}
              excludeColumns={columnID ? [columnID, cond.lhs] : [cond.lhs]}
            />
          )}
          {/*
         <div className='flex-c w-full'>
         {OPERATOR_WITH_CASE_SENSITIVE.includes(cond.operator) && (
         <label className='flex-c'>
         <div>
         <Switch
         size={SwitchSize.XS}
         checked={cond.caseSensitive}
         onClick={(isChecked) => {
         const nextCond = [...value.conditions]
         nextCond[index] = {
         ...nextCond[index],
         caseSensitive: isChecked
         }
         onChange({
         isAny: value.isAny ?? true,
         conditions: nextCond
         })
         }}
         ></Switch>
         </div>
         <div className='text-m-sm whitespace-nowrap'>
         <Trans>Case Sensitive</Trans>
         </div>
         </label>
         )}
         </div>
         */}
        </div>
      </div>
      <div className='flex flex-col gap-2 items-center'>
        <Button
          variant={ButtonVariant.Ghost}
          icon={<Trash16 />}
          className={clsx('shrink-0 w-6 h-9')}
          onClick={() => {
            const nextCond = [...value.conditions]
            nextCond.splice(index, 1)
            onChange({
              operator: defaultOperator,
              conditions: nextCond,
            })
          }}
          noPadding
        />
        <div {...listeners}>
          <Button
            variant={ButtonVariant.Ghost}
            className={clsx('shrink-0 w-6 h-9')}
            defaultSize={false}
            icon={<Reorder16 />}
            noPadding
            title={t('Reorder')}
            noTip
          />
        </div>
      </div>
    </div>
  )
}

interface Props extends FunctionIfsRepeaterProps {
  tables: TableAdapter[]
  columnID?: string
}

export function FunctionIfsRepeater({
  id,
  value,
  onChange,
  colOptions,
  table,
  defaultOperator,
  rhsCustomFieldOnly,
  columnID,
}: Props) {
  const { tables } = useDbClientContext()

  const dragEndCb = useCallback(
    (active: Active, over: Over) => {
      const oldIndex = value?.conditions.findIndex((p) => p.id === active.id)
      const newIndex = value?.conditions.findIndex((p) => p.id === over.id)
      const newValue = arrayMove(value?.conditions, oldIndex, newIndex)
      onChange({
        operator: value?.operator,
        conditions: newValue,
      })
    },
    [value, onChange]
  )

  const { sensors, handleDragEnd } = useSortableHandle(dragEndCb)

  return (
    <div id={id} className='flex flex-col gap-4 w-full'>
      {value && value.conditions.length > 0 && (
        <div className='flex gap-2 flex-col w-full'>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToFirstScrollableAncestor]}
          >
            <SortableContext items={value.conditions}>
              {value.conditions.map((e) => {
                return (
                  <FunctionConditionItem
                    key={e.id || uuid()}
                    expression={e}
                    id={id}
                    onChange={onChange}
                    value={value}
                    colOptions={colOptions}
                    table={table}
                    defaultOperator={defaultOperator}
                    rhsCustomFieldOnly={rhsCustomFieldOnly}
                    columnID={columnID}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        </div>
      )}
      <button
        className='link flex-c'
        id={id ? `${id}-repeat` : undefined}
        onClick={() => {
          onChange({
            operator: defaultOperator,
            conditions: [
              ...(value?.conditions ?? []),
              {
                id: uuid(),
                lhs: colOptions[0]?.id,
                operator: getOperatorDefaultOption(colOptions[0], table, tables),
                caseSensitive: true,
                rhs: undefined,
              },
            ],
          })
        }}
      >
        <Plus16 />
        <span>
          <Trans>Add Criteria</Trans>
        </span>
      </button>
    </div>
  )
}
